import React from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { PassengersCount } from "shared/lib/golObjectTypes/PassengerTypes";
import errorTranslation from "shared/lib/passengersSelect/errorTranslation";

import Counter from "@components/SearchForm/Counter";
import FormattedMessage from "@components/UI/FormattedMessage";
import SimpleSelect from "@components/UI/SimpleSelect";

import { AppState } from "@shared/data/reducers";

import HotelPassengerSelect from "./HotelPassengerSelect";

interface IPassengersSelect {
  isHotelSearchForm: boolean;
  passengers: PassengersCount;
  flightClasses?: Array<{ value: string; label: string }>;
  valueFlightClass?: { value: string; label: string };
  onAddCounterHandler: (type: string) => void;
  onRemoveCounterHandler: (type: string) => void;
  onChangeFlightClass: (flightClass: string) => void;
  allowClassSelection?: boolean;
  defaultFlightClassName?: string;
  displayShowToggleProperties?: boolean;
  toggleShowProperties: () => void;
  error?: boolean;
  errorMessage?: number;
  selectComponentPrefix: string;
  numPassengers: number;
  maxNumPassengers: number;
  setErrorMessage: (messageNumber: number) => void;
}

const PassengersSelect: React.FC<IPassengersSelect> = ({
  isHotelSearchForm,
  passengers,
  onAddCounterHandler,
  onRemoveCounterHandler,
  onChangeFlightClass,
  flightClasses,
  valueFlightClass,
  allowClassSelection,
  defaultFlightClassName,
  displayShowToggleProperties,
  toggleShowProperties,
  error,
  errorMessage,
  selectComponentPrefix,
  numPassengers,
  maxNumPassengers,
  setErrorMessage,
}) => {
  const intl = useIntl();
  const errorTranslations = errorTranslation(intl);

  const { allowedTravelerTypes, hotelSearchSettings } = useSelector(
    (state: AppState) => ({
      allowedTravelerTypes: state.requestorConfig.allowedTravelerTypes,
      hotelSearchSettings: state.requestorConfig.hotelSearchSettings,
    })
  );

  if (isHotelSearchForm) {
    const typedAllowedTravelerTypes = allowedTravelerTypes as Array<{
      Code: string;
      AgeCategory: string;
      $t: string;
    }>;

    return (
      <HotelPassengerSelect
        hotelSearchSettings={hotelSearchSettings}
        allowedTravelerTypes={typedAllowedTravelerTypes}
        selectComponentPrefix={selectComponentPrefix}
        toggleShowProperties={toggleShowProperties}
        setErrorMessage={setErrorMessage}
      />
    );
  }

  return (
    <table className={`${selectComponentPrefix}-properties-table`}>
      <tbody>
        <tr>
          <th
            className={`${selectComponentPrefix}-properties-class-name`}
            id={`${selectComponentPrefix}-GOL_package-textStorage-PropertiesSelect.class`}
          >
            <FormattedMessage id="PropertiesSelect.class" />:
          </th>
          <th className={`${selectComponentPrefix}-properties-class-select`}>
            {allowClassSelection === false ? (
              <span>{defaultFlightClassName?.toUpperCase()}</span>
            ) : (
              <SimpleSelect
                testId="search-properties"
                options={flightClasses}
                onChange={({ value }) => {
                  onChangeFlightClass(value);
                }}
                isSearchable={false}
                value={valueFlightClass}
                disableMenuPortal
              />
            )}
          </th>
        </tr>

        {allowedTravelerTypes.map((allowedTravelerType) => (
          <tr
            key={allowedTravelerType.Code}
            className={`${selectComponentPrefix}-properties-row`}
          >
            <td>
              <span
                id={`${selectComponentPrefix}-GOL_package-textStorage-PassengerCodes.${allowedTravelerType.Code}`}
              >
                {allowedTravelerType.$t}
              </span>{" "}
              {intl.messages[
                `PassengerCodes.${allowedTravelerType.Code}.Description`
              ] && (
                <small>
                  {intl.formatMessage({
                    id: `PassengerCodes.${allowedTravelerType.Code}.Description`,
                  })}
                </small>
              )}
            </td>
            <td>
              <Counter
                type={allowedTravelerType.Code}
                disabledMinus={passengers[allowedTravelerType.Code] === 0}
                disabledPlus={numPassengers >= maxNumPassengers}
                value={passengers[allowedTravelerType.Code]}
                onPlusSign={onAddCounterHandler}
                onMinusSign={onRemoveCounterHandler}
              />
            </td>
          </tr>
        ))}

        <tr className="max-passengers-error">
          {numPassengers >= maxNumPassengers &&
            intl.formatMessage({
              id: "PropertiesSelect.maxPassengersError",
            })}
        </tr>
        {displayShowToggleProperties && (
          <tr>
            {error ? (
              <td
                id={`${selectComponentPrefix}-properties-error-message`}
                style={{
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
              >
                {errorTranslations[errorMessage]}
              </td>
            ) : (
              <td />
            )}
            <td style={{ textAlign: "center" }}>
              <button
                id={`${selectComponentPrefix}-div_toggle_properties_done`}
                onClick={toggleShowProperties}
                className="button button-secondary-color"
                style={{ width: "70%", marginTop: 5 }}
                type="button"
              >
                {intl.formatMessage({
                  id: "PropertiesSelect.done",
                })}
              </button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default PassengersSelect;
